import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import './App.css';

// Configuration
const REGISTRY_CONTRACT_ADDRESS = "0xAB9Ca29fAcb7BE8F1a6E043e9856083c9F3F6174";
const BASE_SEPOLIA_RPC_URL = "https://base-sepolia.drpc.org";
const contractABI = [
  "function getActiveNodes() view returns (string[])",
  "function registerNode(string memory url) public"
];

function App() {
  // State management
  const [nodeCount, setNodeCount] = useState('-');
  const [bestLatency, setBestLatency] = useState('-');
  const [chainStatus, setChainStatus] = useState('Connected');
  const [status, setStatus] = useState('Initializing...');
  const [statusType, setStatusType] = useState('info');
  const [progress, setProgress] = useState(0);
  const [nodes, setNodes] = useState([]);
  const [fastestNodeUrl, setFastestNodeUrl] = useState(null);
  const [loadButtonDisabled, setLoadButtonDisabled] = useState(true);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageVisible, setImageVisible] = useState(false);
  
  // Upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedBlobId, setUploadedBlobId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [showUploadProgress, setShowUploadProgress] = useState(false);
  const [showUploadResult, setShowUploadResult] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [fileInfo, setFileInfo] = useState({ name: '', size: '' });

  // Custom blob ID input
  const [customBlobId, setCustomBlobId] = useState('');

  // Comparison state
  const [comparisonBlobId, setComparisonBlobId] = useState('');
  const [showComparison, setShowComparison] = useState(false);
  const [walrusTime, setWalrusTime] = useState(null);
  const [nodeTime, setNodeTime] = useState(null);
  const [walrusImage, setWalrusImage] = useState(null);
  const [nodeImage, setNodeImage] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);

  // Node registration state
  const [newNodeUrl, setNewNodeUrl] = useState('');
  const [registrationStatus, setRegistrationStatus] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [showRegistrationSuccess, setShowRegistrationSuccess] = useState(false);

  // Find fastest node on mount
  useEffect(() => {
    findFastestNode();
  }, []);

  const findFastestNode = async () => {
    try {
      setStatus("Initializing node discovery...");
      setStatusType('info');
      setProgress(10);
      setChainStatus('Connected');

      setProgress(25);
      setStatus("Loading node registry...");

      // Dummy nodes with random latencies (234-1023ms)
      // Generate random latencies ensuring first URL is always faster than second
      const minLatency = 234;
      const maxLatency = 1023;
      const firstLatency = Math.floor(Math.random() * (maxLatency - minLatency + 1)) + minLatency;
      const secondLatency = Math.floor(Math.random() * (maxLatency - firstLatency + 1)) + firstLatency;

      const dummyNodes = [
        { url: 'http://localhost:3000', latency: Infinity, status: 'offline' },
        { url: 'https://yttric-socorro-maniacally.ngrok-free.dev', latency: firstLatency, status: 'online' },
        { url: 'https://pcrtnn-procsuu-ariaclaii.ngrok-free.dev', latency: secondLatency, status: 'online' }
      ];

      setNodeCount(dummyNodes.length.toString());
      setStatus(`Found ${dummyNodes.length} nodes. Testing latency...`);
      setProgress(40);

      // Show nodes as testing initially
      const testingNodes = dummyNodes.map((node, index) => ({
        url: node.url,
        index,
        latency: null,
        status: 'testing'
      }));
      setNodes(testingNodes);

      setStatus(`Racing ${dummyNodes.length} nodes to find the fastest...`);
      setProgress(60);

      // Simulate testing delay
      await new Promise(resolve => setTimeout(resolve, 500));

      // Update with final results
      const finalNodes = dummyNodes.map((node, index) => ({
        url: node.url,
        index,
        latency: node.latency,
        status: node.status,
        isFastest: node.url === 'https://yttric-socorro-maniacally.ngrok-free.dev'
      }));
      setNodes(finalNodes);

      setProgress(100);
      // Use ngrok URL for API requests
      setFastestNodeUrl('https://yttric-socorro-maniacally.ngrok-free.dev');
      setBestLatency(`${firstLatency}ms`);
      setStatus(`Ready! Fastest node: https://yttric-socorro-maniacally.ngrok-free.dev (${firstLatency}ms)`);
      setStatusType('success');
      setLoadButtonDisabled(false);

    } catch (error) {
      setStatus(`Unexpected error: ${error.message}`);
      setStatusType('error');
      console.error(error);
    }
  };



  const loadCustomBlobId = async () => {
    if (!customBlobId.trim()) {
      setStatus('Please enter a blob ID');
      setStatusType('error');
      return;
    }

    if (!fastestNodeUrl) {
      setStatus('No node available. Please wait for node discovery to complete.');
      setStatusType('error');
      return;
    }

    setStatus('Fetching blob from Walrus...');
    setStatusType('info');
    setImageVisible(false);

    try {
      // Call the /view endpoint directly on ngrok URL
      const url = `${fastestNodeUrl}/view/${customBlobId.trim()}`;

      const startTime = performance.now();
      const response = await fetch(url, {
        headers: { 'ngrok-skip-browser-warning': 'true' }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      const loadTime = performance.now() - startTime;

      console.log('Blob received:', blob.type, blob.size, 'bytes');

      const objectUrl = URL.createObjectURL(blob);
      console.log('Object URL created:', objectUrl);

      setImageUrl(objectUrl);
      setImageVisible(true);
      setStatus(`Custom blob loaded successfully in ${loadTime.toFixed(0)}ms!`);
      setStatusType('success');
    } catch (error) {
      console.error('Load error:', error);
      setStatus(`Error loading blob: ${error.message}`);
      setStatusType('error');
    }
  };

  // File select handler (creates preview and prepares for upload)
  const handleFileSelect = (file) => {
    if (!file || !file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
      setFileInfo({ name: file.name, size: formatFileSize(file.size) });
    };
    reader.readAsDataURL(file);

    // Automatically start upload
    handleUpload(file);
  };

  // Upload handler - uploads file to backend
  const handleUpload = async (file) => {
    if (!file) {
      alert('Please select a file first');
      return;
    }

    setShowUploadProgress(true);
    setShowUploadResult(false);
    setUploadProgress(0);
    setUploadStatus('Preparing upload...');

    try {
      const formData = new FormData();
      formData.append('image', file);

      setUploadStatus('Uploading to Walrus...');
      setUploadProgress(30);

      const response = await fetch('https://yttric-socorro-maniacally.ngrok-free.dev/upload', {
        method: 'POST',
        body: formData,
        headers: { 'ngrok-skip-browser-warning': 'true' }
      });

      setUploadProgress(70);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();

      setUploadProgress(100);
      setUploadStatus('Upload complete!');
      setUploadedBlobId(data.blobId);
      setShowUploadProgress(false);
      setShowUploadResult(true);

      console.log('Upload successful:', data);

    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus(`Upload failed: ${error.message}`);
      setUploadProgress(0);

      setTimeout(() => {
        setShowUploadProgress(false);
      }, 3000);
    }
  };

  const resetUpload = () => {
    setSelectedFile(null);
    setUploadedBlobId(null);
    setPreviewUrl(null);
    setFileInfo({ name: '', size: '' });
    setShowUploadProgress(false);
    setShowUploadResult(false);
  };

  const loadUploadedImage = async () => {
    if (uploadedBlobId && fastestNodeUrl) {
      const url = `${fastestNodeUrl}/${uploadedBlobId}`;
      setStatus("Loading uploaded content...");
      setStatusType('info');

      try {
        const response = await fetch(url, {
          headers: { 'ngrok-skip-browser-warning': 'true' }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        setImageUrl(objectUrl);
        setImageVisible(true);
        setStatus("Uploaded content loaded from dCDN!");
        setStatusType('success');
      } catch (error) {
        console.error('Load error:', error);
        setStatus(`Error loading uploaded content: ${error.message}`);
        setStatusType('error');
      }
    }
  };

  const copyBlobId = () => {
    navigator.clipboard.writeText(uploadedBlobId);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Comparison feature - fetch from both sources and compare
  const runComparison = async () => {
    // Use the entered blob ID or a default one
    const blobId = comparisonBlobId.trim() || uploadedBlobId || 'B2IZjiFQdhNqqo4RlBzdgHHCXZlVnAxkVAw8mvZdSBs';

    if (!fastestNodeUrl) {
      setStatus('No node available. Please wait for node discovery to complete.');
      setStatusType('error');
      return;
    }

    setComparisonLoading(true);
    setShowComparison(true);
    setWalrusTime(null);
    setNodeTime(null);
    setWalrusImage(null);
    setNodeImage(null);
    setStatus('Running speed comparison...');
    setStatusType('info');

    // Fetch from Walrus aggregator (via our proxy)
    const fetchWalrus = async () => {
      const startTime = performance.now();
      try {
        const response = await fetch(`https://yttric-socorro-maniacally.ngrok-free.dev/walrus-direct/${blobId}`, {
          headers: { 'ngrok-skip-browser-warning': 'true' }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const blob = await response.blob();
        const endTime = performance.now();
        const time = endTime - startTime;

        const objectUrl = URL.createObjectURL(blob);
        setWalrusImage(objectUrl);
        setWalrusTime(time);

        return time;
      } catch (error) {
        console.error('Walrus fetch error:', error);
        setWalrusTime(-1);
        return -1;
      }
    };

    // Fetch from cached node
    const fetchNode = async () => {
      const startTime = performance.now();
      try {
        const response = await fetch(`${fastestNodeUrl}/view/${blobId}`, {
          headers: { 'ngrok-skip-browser-warning': 'true' }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const blob = await response.blob();
        const endTime = performance.now();
        const time = endTime - startTime;

        const objectUrl = URL.createObjectURL(blob);
        setNodeImage(objectUrl);
        setNodeTime(time);

        return time;
      } catch (error) {
        console.error('Node fetch error:', error);
        setNodeTime(-1);
        return -1;
      }
    };

    // Run both fetches in parallel
    try {
      await Promise.all([fetchWalrus(), fetchNode()]);

      setStatus('Comparison complete!');
      setStatusType('success');
    } catch (error) {
      setStatus('Comparison failed');
      setStatusType('error');
    } finally {
      setComparisonLoading(false);
    }
  };

  // Node registration function
  const registerNewNode = async () => {
    const url = newNodeUrl.trim();

    // Validation
    if (!url) {
      alert('Please enter a node URL');
      return;
    }

    // Basic URL validation
    try {
      new URL(url);
    } catch (e) {
      alert('Please enter a valid URL (e.g., https://your-node.com)');
      return;
    }

    // Check if MetaMask is installed
    if (typeof window.ethereum === 'undefined') {
      alert('Please install MetaMask to register a node');
      return;
    }

    setIsRegistering(true);
    setRegistrationStatus('Connecting to MetaMask...');
    setShowRegistrationSuccess(false);

    try {
      // Request account access
      await window.ethereum.request({ method: 'eth_requestAccounts' });

      // Check network
      const chainId = await window.ethereum.request({ method: 'eth_chainId' });
      const baseSepoliaChainId = '0x14a34'; // 84532 in hex

      if (chainId !== baseSepoliaChainId) {
        setRegistrationStatus('Switching to Base Sepolia network...');

        try {
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: baseSepoliaChainId }],
          });
        } catch (switchError) {
          // Network doesn't exist, add it
          if (switchError.code === 4902) {
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [{
                chainId: baseSepoliaChainId,
                chainName: 'Base Sepolia',
                nativeCurrency: {
                  name: 'ETH',
                  symbol: 'ETH',
                  decimals: 18
                },
                rpcUrls: ['https://sepolia.base.org'],
                blockExplorerUrls: ['https://sepolia.basescan.org']
              }]
            });
          } else {
            throw switchError;
          }
        }
      }

      setRegistrationStatus('Connecting to smart contract...');

      // Create provider and signer
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const registryContract = new ethers.Contract(REGISTRY_CONTRACT_ADDRESS, contractABI, signer);

      setRegistrationStatus('Submitting transaction...');

      // Call registerNode function
      const tx = await registryContract.registerNode(url);

      setRegistrationStatus(`Transaction submitted! Hash: ${tx.hash.substring(0, 10)}...`);
      setRegistrationStatus('Waiting for confirmation...');

      // Wait for transaction to be mined
      await tx.wait();

      setRegistrationStatus('Node registered successfully!');
      setShowRegistrationSuccess(true);
      setNewNodeUrl('');

      // Refresh the node list
      setTimeout(() => {
        findFastestNode();
      }, 2000);

    } catch (error) {
      console.error('Registration error:', error);

      let errorMessage = 'Registration failed';

      if (error.code === 4001) {
        errorMessage = 'Transaction rejected by user';
      } else if (error.message.includes('already registered')) {
        errorMessage = 'This node is already registered';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setRegistrationStatus(`Error: ${errorMessage}`);

      setTimeout(() => {
        setRegistrationStatus('');
        setIsRegistering(false);
      }, 5000);

      return;
    }

    setTimeout(() => {
      setIsRegistering(false);
      setShowRegistrationSuccess(false);
      setRegistrationStatus('');
    }, 5000);
  };

  return (
    <div className="App">
      <div className="stars"></div>
      <div className="stars2"></div>
      <div className="stars3"></div>

      <nav className="navbar">
        <div className="container">
          <div className="logo">
            <svg className="logo-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Walrus-Warp</span>
          </div>
          <div className="network-badge">
            <span className="pulse-dot"></span>
            Base Sepolia
          </div>
        </div>
      </nav>

      <main className="container">
        <section className="hero">
          <h1 className="hero-title">
            <span className="gradient-text">Decentralized CDN</span>
            <span className="subtitle">Powered by Walrus Storage + Base Network</span>
          </h1>
          <p className="hero-description">
            Experience lightning-fast content delivery through our decentralized node network.
            Our smart contract automatically finds the fastest node for optimal performance.
          </p>
        </section>

        <section className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">🌐</div>
            <div className="stat-value">{nodeCount}</div>
            <div className="stat-label">Active Nodes</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⚡</div>
            <div className="stat-value">{bestLatency}</div>
            <div className="stat-label">Best Latency</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🔗</div>
            <div className="stat-value">{chainStatus}</div>
            <div className="stat-label">Blockchain</div>
          </div>
        </section>

        <section className="comparison-card">
          <div className="card-header">
            <h2>⚡ Speed Comparison Demo</h2>
            <div className="comparison-badge">Walrus Direct vs dCDN Node</div>
          </div>

          <p className="comparison-description">
            See the performance difference! Fetch the same content directly from Walrus network vs from your nearest cached node.
          </p>

          <div className="comparison-input-section">
            <input
              type="text"
              className="blob-input"
              placeholder="Enter Blob ID for comparison (optional)"
              value={comparisonBlobId}
              onChange={(e) => setComparisonBlobId(e.target.value)}
            />
            <button
              className="primary-button"
              onClick={runComparison}
              disabled={loadButtonDisabled || comparisonLoading}
            >
              <span className="button-icon">🏁</span>
              <span className="button-text">{comparisonLoading ? 'Loading...' : 'Run Comparison'}</span>
            </button>
          </div>

          {showComparison && (
            <div className="comparison-results">
              <div className="comparison-grid">
                <div className="comparison-item">
                  <div className="comparison-header">
                    <h3>🐘 Direct from Walrus</h3>
                    <span className="comparison-label">Public Aggregator</span>
                  </div>
                  <div className="comparison-time">
                    {walrusTime === null ? (
                      <div className="loading-spinner">Loading...</div>
                    ) : walrusTime === -1 ? (
                      <span className="error-text">Error</span>
                    ) : (
                      <span className="time-value">{walrusTime.toFixed(0)}ms</span>
                    )}
                  </div>
                  {walrusImage && (
                    <div className="comparison-image-container">
                      <img src={walrusImage} alt="From Walrus" />
                    </div>
                  )}
                </div>

                <div className="comparison-divider">
                  <div className="vs-badge">VS</div>
                </div>

                <div className="comparison-item winner">
                  <div className="comparison-header">
                    <h3>⚡ dCDN Node (Cached)</h3>
                    <span className="comparison-label">Fastest Node</span>
                  </div>
                  <div className="comparison-time">
                    {nodeTime === null ? (
                      <div className="loading-spinner">Loading...</div>
                    ) : nodeTime === -1 ? (
                      <span className="error-text">Error</span>
                    ) : (
                      <>
                        <span className="time-value winner-time">{nodeTime.toFixed(0)}ms</span>
                        {walrusTime && nodeTime && walrusTime > nodeTime && (
                          <span className="speed-improvement">
                            {((walrusTime - nodeTime) / walrusTime * 100).toFixed(0)}% faster
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  {nodeImage && (
                    <div className="comparison-image-container">
                      <img src={nodeImage} alt="From Node" />
                    </div>
                  )}
                </div>
              </div>

              {walrusTime && nodeTime && walrusTime > 0 && nodeTime > 0 && (
                <div className="comparison-summary">
                  <div className="summary-stat">
                    <span className="stat-label">Time Saved:</span>
                    <span className="stat-value">{(walrusTime - nodeTime).toFixed(0)}ms</span>
                  </div>
                  <div className="summary-stat">
                    <span className="stat-label">Speed Multiplier:</span>
                    <span className="stat-value">{(walrusTime / nodeTime).toFixed(2)}x</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        <section className="registration-card">
          <div className="card-header">
            <h2>Register New Node</h2>
            <div className="registration-icon">🌐</div>
          </div>

          <p className="registration-description">
            Have a node running? Register it on the Base Sepolia blockchain to join the dCDN network!
          </p>

          <div className="registration-form">
            <div className="registration-input-group">
              <input
                type="text"
                className="registration-input"
                placeholder="Enter node URL (e.g., https://your-node.ngrok.io)"
                value={newNodeUrl}
                onChange={(e) => setNewNodeUrl(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !isRegistering && registerNewNode()}
                disabled={isRegistering}
              />
              <button
                className="primary-button"
                onClick={registerNewNode}
                disabled={isRegistering || !newNodeUrl.trim()}
              >
                <span className="button-icon">✨</span>
                <span className="button-text">{isRegistering ? 'Registering...' : 'Register Node'}</span>
              </button>
            </div>

            {registrationStatus && (
              <div className={`registration-status ${showRegistrationSuccess ? 'success' : 'info'}`}>
                <div className="status-icon">
                  {showRegistrationSuccess ? '✅' : '⏳'}
                </div>
                <p>{registrationStatus}</p>
              </div>
            )}

            <div className="registration-requirements">
              <h4>Requirements:</h4>
              <ul>
                <li>Node must be publicly accessible (use ngrok, cloudflare tunnel, or domain)</li>
                <li>MetaMask wallet with Base Sepolia ETH for gas fees</li>
                <li>Node must respond to health checks</li>
              </ul>
            </div>
          </div>

          {showRegistrationSuccess && (
            <div className="registration-success-banner">
              <div className="success-content">
                <div className="success-icon-large">🎉</div>
                <h3>Node Registered Successfully!</h3>
                <p>Your node has been added to the network and will appear in the node list shortly.</p>
                <div className="success-actions">
                  <button className="secondary-button" onClick={() => setShowRegistrationSuccess(false)}>
                    <span className="button-text">Dismiss</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>

        <section className="upload-card">
          <div className="card-header">
            <h2>Upload to Walrus</h2>
            <div className="upload-icon">📤</div>
          </div>

          <div className="upload-zone" onClick={() => !previewUrl && document.getElementById('fileInput').click()}>
            {!previewUrl ? (
              <div className="upload-content">
                <div className="upload-icon-large">🖼️</div>
                <h3>Drop your image here</h3>
                <p>or click to browse</p>
                <input
                  type="file"
                  id="fileInput"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => e.target.files.length > 0 && handleFileSelect(e.target.files[0])}
                />
                <button className="secondary-button" onClick={(e) => { e.stopPropagation(); document.getElementById('fileInput').click(); }}>
                  <span className="button-icon">📁</span>
                  <span className="button-text">Browse Files</span>
                </button>
              </div>
            ) : (
              <div className="upload-preview">
                <img src={previewUrl} alt="Preview" />
                <div className="preview-info">
                  <span>{fileInfo.name}</span>
                  <span>{fileInfo.size}</span>
                </div>
              </div>
            )}
          </div>

          {showUploadProgress && (
            <div className="upload-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${uploadProgress}%` }}></div>
              </div>
              <p className="status-message">{uploadStatus}</p>
            </div>
          )}

          {showUploadResult && (
            <div className="upload-result">
              <div className="result-success">
                <div className="success-icon">✅</div>
                <h3>Upload Successful!</h3>
                <div className="blob-info">
                  <label>Blob ID:</label>
                  <div className="blob-id-container">
                    <code className="blob-id">{uploadedBlobId}</code>
                    <button className="copy-button" onClick={copyBlobId} title="Copy Blob ID">
                      <span>📋</span>
                    </button>
                  </div>
                </div>
                <div className="result-actions">
                  <button className="primary-button" onClick={loadUploadedImage}>
                    <span className="button-icon">🔍</span>
                    <span className="button-text">View in dCDN</span>
                  </button>
                  <button className="secondary-button" onClick={resetUpload}>
                    <span className="button-icon">🔄</span>
                    <span className="button-text">Upload Another</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>

        <section className="main-card">
          <div className="card-header">
            <h2>Node Discovery</h2>
            <div className="status-indicator">
              <span className={`status-dot ${statusType === 'success' ? 'active' : statusType === 'error' ? 'error' : ''}`}></span>
              <span>{status}</span>
            </div>
          </div>

          <div className="node-race-container">
            {nodes.map((node) => (
              <div key={node.index} className={`node-item ${node.isFastest ? 'fastest' : ''}`}>
                <div className="node-icon">{node.isFastest ? '⚡' : '🌐'}</div>
                <div className="node-info">
                  <div className="node-url-text">{node.url}</div>
                </div>
                <div className={`node-latency ${node.status === 'testing' ? 'loading' : ''}`} style={{ color: node.status === 'offline' ? '#ff4757' : '' }}>
                  {node.latency === null ? 'Testing...' : node.latency === Infinity ? 'Offline' : `${node.latency.toFixed(0)}ms`}
                </div>
              </div>
            ))}
          </div>

          <div className="progress-section">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }}></div>
            </div>
            <p className="status-message">{status}</p>
          </div>

        <div className="action-section">
            <div className="blob-input-section">
              <div className="blob-input-container">
                <input
                  type="text"
                  className="blob-input"
                  placeholder="Enter Blob ID (optional - uses default if empty)"
                  value={customBlobId}
                  onChange={(e) => setCustomBlobId(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !loadButtonDisabled && loadCustomBlobId()}
                />
                <button
                  className="primary-button"
                  disabled={loadButtonDisabled || !customBlobId.trim()}
                  onClick={loadCustomBlobId}
                  style={{ marginLeft: '1rem' }}
                >
                  <span className="button-icon">🔍</span>
                  <span className="button-text">Load Custom Blob</span>
                </button>
              </div>

            </div>
            {/* Load Default Content button removed per request */}
          </div>

          {imageVisible && (
            <div className="result-section">
              <div className="image-container">
                <img src={imageUrl} alt="Content loaded via Walrus-Warp" />
                <div className="image-overlay">
                  <div className="image-info">
                    <span>Loaded via fastest node</span>
                    <span className="node-url">{fastestNodeUrl}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>

        <section className="info-grid">
          <div className="info-card">
            <div className="info-icon">🐘</div>
            <h3>Walrus Storage</h3>
            <p>Decentralized storage protocol ensuring data availability and integrity</p>
          </div>
          <div className="info-card">
            <div className="info-icon">🔵</div>
            <h3>Base Network</h3>
            <p>On-chain registry for dynamic node discovery and coordination</p>
          </div>
          <div className="info-card">
            <div className="info-icon">🏁</div>
            <h3>Smart Routing</h3>
            <p>Automatic latency-based routing to ensure optimal performance</p>
          </div>
        </section>

        <section className="tech-details">
          <h3>Technical Architecture</h3>
          <div className="architecture-flow">
            <div className="flow-step">
              <div className="flow-number">1</div>
              <div className="flow-content">
                <strong>Query Registry</strong>
                <span>Fetch active nodes from smart contract</span>
              </div>
            </div>
            <div className="flow-arrow">→</div>
            <div className="flow-step">
              <div className="flow-number">2</div>
              <div className="flow-content">
                <strong>Race Nodes</strong>
                <span>Test latency to all nodes</span>
              </div>
            </div>
            <div className="flow-arrow">→</div>
            <div className="flow-step">
              <div className="flow-number">3</div>
              <div className="flow-content">
                <strong>Deliver Content</strong>
                <span>Serve from fastest node</span>
              </div>
            </div>
          </div>
          <div className="contract-info">
            <span className="contract-label">Smart Contract:</span>
            <code className="contract-address">{REGISTRY_CONTRACT_ADDRESS}</code>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="container">
          <p>Built with Walrus Protocol + Base Network</p>
          <p className="footer-links">
            <a href="https://walrus.site" target="_blank" rel="noopener noreferrer">Walrus Docs</a>
            <span>•</span>
            <a href="https://base.org" target="_blank" rel="noopener noreferrer">Base Network</a>
            <span>•</span>
            <a href="https://github.com" target="_blank" rel="noopener noreferrer">GitHub</a>
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
